import { useMemo } from 'react';
import { turnoverAssetTypesFCA } from '../../../../consts/assetTypes';
import { updateDataByType, getColumnByType, getCellWidths, getRowHeight } from '../utils/functions';
import commonStore from '../../../../store/common';
import { CustomHeaderWithDrillDown } from '../../components/CustomHeaderWithDrillDown';
import { useSearchParams } from 'react-router-dom';
import { reaction } from 'mobx';
import reportStore from '../../store';
import { useSyncExternalStore } from 'react';
import { UseTurnoverProps, UseTurnoverReturn } from './types';
import { isFcaTableDisabledForFund } from '../utils/visibleSections';
import { deepCopy } from '../../../../utils/deepCopy';
const tableSchema = {
  Securities: {
    SEC_CSH_CSH: {
      market_value: null
    },
    SEC_LEQ_LEQ: {
      market_value: null
    },
    SEC_UEQ_UEQ: {
      market_value: null
    }
  },
  'Corporate bonds not issued by financial institutions - which are': {
    SEC_CPN_IVG: {
      market_value: null
    },
    SEC_CPN_NIG: {
      market_value: null
    },
    SEC_CPI_CPI: {
      market_value: null
    }
  },
  'Sovereign  bonds - which are': {
    SEC_SBD_UKB: {
      market_value: null
    },
    SEC_SBD_NUK: {
      market_value: null
    },
    SEC_MUN_MUN: {
      market_value: null
    },
    SEC_CBD_CBD: {
      market_value: null
    },
    SEC_LON_LON: {
      market_value: null
    },
    SEC_SSP_SSP: {
      market_value: null
    }
  },
  Derivatives: {
    DER_EQD_EQD: {
      market_value: null,
      notional_value: null
    },
    DER_FID_FID: {
      market_value: null,
      notional_value: null
    },
    DER_CDS_CDS: {
      market_value: null,
      notional_value: null
    },
    DER_FEX_INV: {
      market_value: null,
      notional_value: null
    },
    DER_FEX_HED: {
      market_value: null,
      notional_value: null
    },
    DER_IRD_IRD: {
      market_value: null,
      notional_value: null
    },
    DER_CTY_CTY: {
      market_value: null,
      notional_value: null
    },
    DER_OTH_OTH: {
      market_value: null,
      notional_value: null
    }
  },
  'Physical (Real/Tangible) Assets': {
    PHY_RES_RES: {
      market_value: null
    },
    PHY_CTY_CTY: {
      market_value: null
    },
    PHY_TIM_TIM: {
      market_value: null
    },
    PHY_ART_ART: {
      market_value: null
    },
    PHY_TPT_TPT: {
      market_value: null
    },
    PHY_OTH_OTH: {
      market_value: null
    },
    CIU_CIU_CIU: {
      market_value: null
    }
  },
  'Total in other asset classes': {
    OTH_OTH_OTH: {
      market_value: null
    }
  }
};

const tabsKey = {
  market_value: { name: 'Market value', letter: 'A' },
  notional_value: { name: 'Notional value', letter: 'B' }
};

export default function useTurnover({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  isMultiFunds,
  funds
}: UseTurnoverProps): UseTurnoverReturn {
  const [searchParams] = useSearchParams();
  const currentDataKey = searchParams.get('fund_id')!;
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isFcaTableDisabledForFund(fund, 'TURNOVER');
  };
  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );
  const onChange = value => {
    const changes = value[0];
    const _fund = isMultiFunds
      ? deepCopy(funds[changes.newCell.fundId].fund_data['TURNOVER'])
      : deepCopy(data);
    const index = _fund.findIndex(item => item.sub_asset_type === changes.newCell.sub_asset_type);

    _fund[index][changes.newCell.dataKey] = updateDataByType({
      cell: changes.newCell
    });

    onChangeFund('TURNOVER', _fund, changes.newCell.fundId);
  };
  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );

  const tables = useMemo<UseTurnoverReturn['tables']>(() => {
    if (!data) return [];

    const { labelWidth, valueWidth } = getCellWidths({
      isMultiFunds,
      isComparisonReport,
      fundSize: Object.keys(funds).length,
      contentWidth
    });

    let amountRow = 147;
    const _fundSize = Object.keys(funds).length;
    const _tables = Object.keys(tableSchema).map(macro_asset_type => {
      const _colums = tableSchema[macro_asset_type][Object.keys(tableSchema[macro_asset_type])[0]];

      const assetTypes = tableSchema[macro_asset_type];
      amountRow = amountRow + Object.keys(assetTypes).length;

      return {
        name: macro_asset_type,
        tables: Object.keys(_colums).map(columnKey => {
          return {
            columns: isMultiFunds
              ? _fundSize <= 3 && !isComparisonReport
                ? [
                    {
                      columnId: 0,
                      resizable: true,
                      width: 30
                    },
                    {
                      columnId: 1,
                      resizable: true,
                      width: 30
                    },
                    ...Array.from(Array(_fundSize + 1).keys()).map(index => ({
                      columnId: index + 2,
                      width: contentWidth / (_fundSize + 1)
                    }))
                  ]
                : isComparisonReport
                  ? [
                      {
                        columnId: 0,
                        resizable: true,
                        width: 30
                      },
                      {
                        columnId: 1,
                        resizable: true,
                        width: 30
                      },
                      {
                        columnId: 2,
                        width: 200
                      },
                      ...Object.keys(funds)
                        .map((item: any) => [item, item.previous_fund])
                        .flat()
                        .map((_, index) => ({
                          columnId: index + 3,
                          width: 300
                        }))
                    ]
                  : [
                      {
                        columnId: 0,
                        resizable: true,
                        width: 30
                      },
                      {
                        columnId: 1,
                        resizable: true,
                        width: 30
                      },
                      {
                        columnId: 2,
                        width: 200
                      },
                      ...Object.keys(funds).map((_, index) => ({
                        columnId: index + 3,
                        width: 300
                      }))
                    ]
              : isComparisonReport
                ? [
                    {
                      columnId: 0,
                      resizable: true,
                      width: 30
                    },
                    {
                      columnId: 1,
                      resizable: true,
                      width: 30
                    },
                    ...Array.from(Array(3).keys()).map(index => ({
                      columnId: index + 2,
                      width: contentWidth / 3
                    }))
                  ]
                : [
                    {
                      columnId: 0,
                      resizable: true,
                      width: 30
                    },
                    {
                      columnId: 1,
                      resizable: true,
                      width: 30
                    },
                    ...Array.from(Array(2).keys()).map(index => ({
                      columnId: index + 2,
                      width: contentWidth / 2
                    }))
                  ],
            rows: [
              {
                height: isMultiFunds ? 50 : 40,
                rowId: 0,
                cells: isMultiFunds
                  ? [
                      {
                        type: 'header',
                        text: ''
                      },
                      {
                        type: 'header',
                        text: ''
                      },
                      {
                        type: 'header',
                        text: tabsKey[columnKey]?.name || ''
                      },
                      ...(isComparisonReport
                        ? Object.values(funds)
                            .map(item => [item, item.previous_fund])
                            .flat()
                            .map((fund, i) => ({
                              type: 'header',
                              text: fund!.fund_name || '',
                              colspan: i % 2 === 0 ? 2 : undefined
                            }))
                        : Object.values(funds).map(fund => ({
                            type: 'header',
                            text: fund.fund_name || ''
                          })))
                    ]
                  : [
                      {
                        type: 'header',
                        text: ''
                      },
                      {
                        type: 'header',
                        text: ''
                      },
                      {
                        type: 'header',
                        text: tabsKey[columnKey]?.name || ''
                      },
                      ...(isComparisonReport
                        ? [
                            {
                              type: 'header',
                              text: `${funds[currentDataKey].previous_fund?.period}, ${funds[currentDataKey].previous_fund?.year}`
                            },
                            {
                              type: 'header',
                              text: `${funds[currentDataKey].period}, ${funds[currentDataKey].year}`
                            }
                          ]
                        : [
                            {
                              type: 'header',
                              text: ''
                            }
                          ])
                    ]
              },
              ...(isComparisonReport && isMultiFunds
                ? [
                    {
                      height: 50,
                      rowId: 1,
                      cells: [
                        {
                          type: 'header',
                          text: ''
                        },
                        {
                          type: 'header',
                          text: ''
                        },
                        {
                          type: 'header',
                          text: ''
                        },
                        ...Object.values(funds)
                          .map((item: any) => [item.previous_fund, item])
                          .flat()
                          .map(fund => ({
                            type: 'header',
                            text: `${fund.period}, ${fund.year}`
                          }))
                      ]
                    }
                  ]
                : []),
              ...Object.keys(assetTypes).map((sub_asset_type, index) => {
                // amountRow = amountRow + 1;
                const __value = data.find(val => val.sub_asset_type === sub_asset_type) as any;
                const __previous_value = funds[
                  currentDataKey
                ]?.previous_fund?.fund_data?.TURNOVER?.find(
                  val => val.sub_asset_type === sub_asset_type
                );
                const __differenceList = (funds[currentDataKey] as any)?.difference?.['TURNOVER'];
                const __difference = __differenceList
                  ? __differenceList.find(val => val.sub_asset_type === sub_asset_type)
                  : null;
                const number = amountRow - Object.keys(assetTypes).length + index + 1;

                const cells = !isMultiFunds
                  ? [
                      {
                        type:
                          __value?.report_id &&
                          schema.types[columnKey].has_drill_down_feature &&
                          __value?.[columnKey] !== null
                            ? 'customHeader'
                            : 'header',
                        text: turnoverAssetTypesFCA[sub_asset_type] || '',
                        render: () => (
                          <CustomHeaderWithDrillDown
                            reportId={__value?.report_id}
                            fundId={__value.fund_id}
                            columnName={columnKey}
                            tableName="TURNOVER"
                            title={turnoverAssetTypesFCA[sub_asset_type] || ''}
                            tableId={__value?.id}
                          />
                        ),
                        style: {
                          'justify-content': 'flex-start',
                          'white-space': 'inherit'
                        }
                      },
                      ...(isComparisonReport
                        ? [
                            {
                              ...getColumnByType({
                                type: schema.types[columnKey].type,
                                options: schema.types[columnKey].options,
                                key: columnKey,
                                defaultValue: schema.defaultValues[columnKey],
                                index: 0,
                                value: __previous_value,
                                required: requiredFields.includes(columnKey),
                                tableName: 'TURNOVER',
                                isStaticTemplate,
                                isMultiFunds,
                                nonEditable: isComparisonReport,
                                fundId: __previous_value?.fund_id,
                                isSectionDisabled: isTableDisabledForFund(__previous_value),
                                fundName: __previous_value?.fund_name,
                                fieldOrder: number
                              }),
                              sub_asset_type
                            },
                            {
                              ...getColumnByType({
                                type: schema.types[columnKey].type,
                                options: schema.types[columnKey].options,
                                key: columnKey,
                                defaultValue: schema.defaultValues[columnKey],
                                index: 0,
                                value: __value,
                                required: requiredFields.includes(columnKey),
                                tableName: 'TURNOVER',
                                isStaticTemplate,
                                isMultiFunds,
                                nonEditable: isComparisonReport,
                                fundId: __value?.fund_id,
                                isSectionDisabled: isTableDisabledForFund(__value?.fund_id),
                                fundName: __value?.fund_name,
                                fieldOrder: number,
                                difference: __difference?.[columnKey] || {}
                              }),
                              sub_asset_type
                            }
                          ]
                        : [
                            {
                              ...getColumnByType({
                                type: schema.types[columnKey].type,
                                options: schema.types[columnKey].options,
                                key: columnKey,
                                defaultValue: schema.defaultValues[columnKey],
                                index: 0,
                                value: __value,
                                required: requiredFields.includes(columnKey),
                                tableName: 'TURNOVER',
                                isStaticTemplate,
                                isMultiFunds,
                                nonEditable: isComparisonReport,
                                fundId: __value?.fund_id,
                                isSectionDisabled: isTableDisabledForFund(__value?.fund_id),
                                fundName: __value?.fund_name,
                                fieldOrder: number
                              }),
                              sub_asset_type
                            }
                          ])
                    ]
                  : [
                      {
                        type: 'header',
                        text: turnoverAssetTypesFCA[sub_asset_type] || '',
                        style: {
                          'justify-content': 'flex-start',
                          'white-space': 'inherit'
                        }
                      },
                      ...(isComparisonReport
                        ? Object.values(funds)
                            .map((item: any) => [item.previous_fund, item])
                            .flat()
                            .map((value, j) => {
                              const _value = value.fund_data.TURNOVER.find(
                                val => val.sub_asset_type === sub_asset_type
                              );
                              const __differenceList = (funds[value.fund_id] as any)?.difference?.[
                                'TURNOVER'
                              ];
                              const __difference = __differenceList
                                ? __differenceList.find(
                                    val => val.sub_asset_type === sub_asset_type
                                  )
                                : null;
                              return {
                                ...getColumnByType({
                                  type: schema.types[columnKey].type,
                                  options: schema.types[columnKey].options,
                                  key: columnKey,
                                  defaultValue: schema.defaultValues[columnKey],
                                  index: 0,
                                  value: _value,
                                  required: requiredFields.includes(columnKey),
                                  tableName: 'TURNOVER',
                                  isStaticTemplate,
                                  hasDrillDownFeature:
                                    !!_value?.report_id &&
                                    schema.types[columnKey].has_drill_down_feature &&
                                    _value?.[columnKey] !== null,
                                  reportId: _value?.report_id,
                                  tableId: _value?.id,
                                  title: turnoverAssetTypesFCA[sub_asset_type],
                                  isMultiFunds,
                                  nonEditable: isComparisonReport,
                                  fundId: _value?.fund_id,
                                  isSectionDisabled: isTableDisabledForFund(_value?.fund_id),
                                  fundName: _value?.fund_name,
                                  fieldOrder: number,
                                  difference: j % 2 === 1 ? __difference?.[columnKey] || {} : null
                                }),
                                sub_asset_type
                              };
                            })
                        : Object.values(funds).map(value => {
                            const _value = value.fund_data.TURNOVER.find(
                              val => val.sub_asset_type === sub_asset_type
                            );
                            return {
                              ...getColumnByType({
                                type: schema.types[columnKey].type,
                                options: schema.types[columnKey].options,
                                key: columnKey,
                                defaultValue: schema.defaultValues[columnKey],
                                index: 0,
                                value: _value,
                                required: requiredFields.includes(columnKey),
                                tableName: 'TURNOVER',
                                isStaticTemplate,
                                hasDrillDownFeature:
                                  !!_value?.report_id &&
                                  schema.types[columnKey].has_drill_down_feature &&
                                  _value?.[columnKey] !== null,
                                reportId: _value?.report_id,
                                tableId: _value?.id,
                                title: turnoverAssetTypesFCA[sub_asset_type],
                                isMultiFunds,
                                nonEditable: isComparisonReport,
                                fundId: _value?.fund_id,
                                isSectionDisabled: isTableDisabledForFund(_value?.fund_id),
                                fundName: _value?.fund_name,
                                fieldOrder: number
                              }),
                              sub_asset_type
                            };
                          }))
                    ];

                const valueCells = isMultiFunds
                  ? (isComparisonReport
                      ? Object.values(funds)
                          .map(item => [item.previous_fund, item])
                          .flat()
                      : Object.values(funds)
                    ).map(fund => {
                      const row = fund!.fund_data.TURNOVER.find(
                        item => item.sub_asset_type == sub_asset_type
                      );
                      return {
                        value: '0',
                        error: (row?.errors?.[columnKey] as any)?.msg || ''
                      };
                    })
                  : isComparisonReport
                    ? [
                        {
                          value: '0',
                          error: (__previous_value?.errors?.[columnKey] as any)?.msg || ''
                        },
                        {
                          value: '0',
                          error: __value?.errors?.[columnKey]?.msg || ''
                        }
                      ]
                    : [
                        {
                          value: '0',
                          error: __value?.errors?.[columnKey]?.msg || ''
                        }
                      ];

                const rowHeight = getRowHeight({
                  label: turnoverAssetTypesFCA[sub_asset_type] || '',
                  labelWidth,
                  cells: valueCells,
                  valueWidth
                });
                return {
                  rowId: isComparisonReport && isMultiFunds ? index + 2 : index + 1,
                  height: rowHeight,
                  cells: [
                    {
                      type: 'header',
                      text: `${number === 154 ? 254 : number === 155 ? 255 : number}`
                    },
                    {
                      type: 'header',
                      text: tabsKey[columnKey]?.letter || ''
                    },
                    ...cells
                  ]
                };
              })
            ]
          };
        })
      };
    });

    return _tables;
  }, [data, contentWidth, funds, isMultiFunds, isComparisonReport, currentDataKey]);

  return { onChange, tables };
}
